import { useFormik } from "formik";
import React, { useEffect } from "react";
import { useHistory } from "react-router-dom";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";

import { useToast } from "hooks/Toast";
import {
  useRetainAccountMutation,
  useGetAuthenticatedUserQuery,
} from "generated/graphql";
import Input from "components/molecules/Input";
import Header from "components/atoms/Header";
import { email } from "helpers/formValidation";
import LoginTheme from "themes/LoginTheme";
import paths from "routes/paths";

import {
  FormWrapper,
  HeaderWrapper,
  ButtonsWrapper,
  SpaceMiddle,
  PullRight,
} from "./styles";

const retainAccountInitialValues = {
  email: "",
  confirmEmail: "",
};

const RetainAccess = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const Toast = useToast();

  const searchParams = new URLSearchParams(window.location.search);
  const token = searchParams.get("token");

  const { loading: authLoading, data: authData } = useGetAuthenticatedUserQuery(
    {
      fetchPolicy: "network-only",
    }
  );

  const [
    retainAccount,
    { loading: retainAccountLoading },
  ] = useRetainAccountMutation();

  const signInForm = useFormik({
    initialValues: retainAccountInitialValues,
    validationSchema: Yup.object({
      email,
      confirmEmail: Yup.string()
        .oneOf([Yup.ref("email")], "Emails must match")
        .required("Confirm email is required"),
    }),
    onSubmit: (values) => {
      retainAccount({
        variables: {
          email: values.email,
          token: token || "",
        },
      })
        .then((res) => {
          if (!res.data?.retainAccount?.success) {
            Toast("error", "Something went wrong");
          } else {
            Toast("success", "Email changed successfully");
            history.push(paths.signIn);
          }
        })
        .catch((err) => {
          Toast("error", err.message || t("errorAction.generalError"));
        });
    },
  });

  const handleSignInNavigate = () => {
    history.push(paths.signIn);
  };

  useEffect(() => {
    if (!authLoading && !authData?.authenticatedItem) {
      history.push(`${paths.signIn}?token=${token}`);
    }
  }, [authData, authLoading, history, token]);

  return (
    <LoginTheme>
      <FormWrapper
        onSubmit={signInForm.handleSubmit}
        method="post"
        autoComplete="off"
      >
        <HeaderWrapper>
          <Header>Retain your account</Header>
        </HeaderWrapper>

        <Input
          name="email"
          label="New email to use"
          handleChange={signInForm.handleChange}
          handleBlur={signInForm.handleBlur}
          placeholder={t("user.yourEmail")}
          value={signInForm.values.email}
          error={signInForm.touched.email && signInForm.errors.email}
        />

        <Input
          name="confirmEmail"
          label="Confirm new email"
          handleChange={signInForm.handleChange}
          handleBlur={signInForm.handleBlur}
          placeholder="Confirm new email"
          value={signInForm.values.confirmEmail}
          error={
            signInForm.touched.confirmEmail && signInForm.errors.confirmEmail
          }
        />

        <ButtonsWrapper>
          <SpaceMiddle
            type="submit"
            variant="primary"
            disabled={retainAccountLoading}
          >
            {retainAccountLoading ? "Retaining..." : "Retain account"}
          </SpaceMiddle>
          <PullRight
            type="button"
            variant="link"
            onClick={handleSignInNavigate}
          >
            {t("user.returnToLogin")}
          </PullRight>
        </ButtonsWrapper>
      </FormWrapper>
    </LoginTheme>
  );
};

export default RetainAccess;
